// Nav now uses /frac-logo-official-nav.png directly (see SiteNav.jsx).
// FracLogoLight retained for footer and 404 which remain on dark surfaces.
//
// Logo constructed as inline SVG from client-supplied definitive reference.
//
// Hex mark: 5 white rounded squares on a 3-column × 4-row grid, positions
// (col, row) = (1,1) (3,1) (2,2) (2,3) (1,4). It's a stylised angular F-ish
// glyph whose diagonal step-down (3,1)→(2,2)→(2,3)→(1,4) is the dynamic
// tell. Rounded corners ~2px on 12px squares.
//
// The previous "pixel-F" 6-square patterns (2×4) were all client-superseded;
// this 5-square 3×4 pattern is the current, correct mark.
//
// To swap in an official raster/vector asset when it lands:
//   1. Drop the file at /app/frontend/public/frac-logo-light.svg (or .png)
//   2. Replace <Mark /> below with <img src="/frac-logo-light.svg" alt="Frac" />,
//      OR keep inline for markOnly/wordmarkOnly and use <img> for the full lockup.
//   3. Regenerate raster derivatives via /app/scripts/gen_assets.py so
//      favicon / apple-touch / og-default match the shipped mark.
// Keep the same prop API so no consumer code changes.

import React, { useId } from "react";

const HEIGHTS = { sm: 24, md: 32, lg: 48 };

// Pointy-top hexagon centred at (50,50), radius ~46
const HEX_POINTS = "50,4 91,27 91,73 50,96 9,73 9,27";

// 5 rounded squares on a 3-column × 4-row grid centred at (50, 50).
// Square size 12, gap 2 → 14-unit stride. Column x: 30 / 44 / 58.
// Row y: 23 / 37 / 51 / 65.
//   Row 1:  ■  .  ■   (top-left, top-right — gap at col 2)
//   Row 2:  .  ■  .   (centre step-down)
//   Row 3:  .  ■  .   (stem)
//   Row 4:  ■  .  .   (foot, offset left from the stem)
const PIXEL_F = [
  { x: 30, y: 23 }, // (1,1) top-left
  { x: 58, y: 23 }, // (3,1) top-right
  { x: 44, y: 37 }, // (2,2) centre row 2
  { x: 44, y: 51 }, // (2,3) centre row 3
  { x: 30, y: 65 }, // (1,4) bottom-left foot
];
const SQ = 12;
const SQ_R = 2;

const WORDMARK_STYLE = {
  fontFamily:
    "'Hanken Grotesk', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontWeight: 700,
  fontSize: 88,
  letterSpacing: "-1.8px",
};

function Mark({ hexFill, squareFill, gradientId }) {
  return (
    <>
      {gradientId && (
        <defs>
          <radialGradient
            id={gradientId}
            cx="75%"
            cy="75%"
            r="120%"
            fx="75%"
            fy="75%"
          >
            <stop offset="0%" stopColor="#7200DB" />
            <stop offset="55%" stopColor="#2A0A4A" />
            <stop offset="100%" stopColor="#02040B" />
          </radialGradient>
        </defs>
      )}
      <polygon
        points={HEX_POINTS}
        fill={hexFill}
        stroke={hexFill}
        strokeWidth="6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <g fill={squareFill}>
        {PIXEL_F.map((s, i) => (
          <rect
            key={i}
            x={s.x}
            y={s.y}
            width={SQ}
            height={SQ}
            rx={SQ_R}
            ry={SQ_R}
          />
        ))}
      </g>
    </>
  );
}

function FracLogo({
  variant = "light",
  size = "md",
  wordmarkOnly = false,
  markOnly = false,
  className = "",
  "data-testid": testId,
}) {
  const H = HEIGHTS[size] ?? HEIGHTS.md;
  // "dark" variant: hex has violet gradient, squares WHITE, wordmark PURPLE — used on LIGHT surfaces
  // "light" variant: hex is WHITE, squares PURPLE, wordmark WHITE — used on DARK surfaces
  const useGradient = variant === "dark";
  const rawId = useId().replace(/[^a-zA-Z0-9-]/g, "-");
  const gradientId = useGradient ? `frac-grad-${rawId}` : null;

  const hexFill = useGradient ? `url(#${gradientId})` : "#EDF1F5";
  const squareFill = useGradient ? "#EDF1F5" : "#2A0A4A";
  const wordFill = useGradient ? "#2A0A4A" : "#EDF1F5";

  const label = markOnly
    ? "Frac mark"
    : wordmarkOnly
      ? "Frac wordmark"
      : "Frac";

  if (markOnly) {
    return (
      <svg
        role="img"
        aria-label={label}
        viewBox="0 0 100 100"
        width={H}
        height={H}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        data-testid={testId ?? "frac-logo-mark"}
      >
        <Mark
          hexFill={hexFill}
          squareFill={squareFill}
          gradientId={gradientId}
        />
      </svg>
    );
  }

  if (wordmarkOnly) {
    const W = H * 2.4;
    return (
      <svg
        role="img"
        aria-label={label}
        viewBox="0 0 240 100"
        width={W}
        height={H}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        data-testid={testId ?? "frac-logo-wordmark"}
      >
        <text x="0" y="78" style={WORDMARK_STYLE} fill={wordFill}>
          FRAC
        </text>
      </svg>
    );
  }

  const W = Math.round(H * 3.4);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox="0 0 340 100"
      width={W}
      height={H}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid={testId ?? "frac-logo"}
    >
      <Mark hexFill={hexFill} squareFill={squareFill} gradientId={gradientId} />
      <text x="120" y="78" style={WORDMARK_STYLE} fill={wordFill}>
        FRAC
      </text>
    </svg>
  );
}

export const FracLogoLight = (props) => <FracLogo {...props} variant="light" />;
export const FracLogoDark = (props) => <FracLogo {...props} variant="dark" />;
export default FracLogo;
